import { AIScanner } from '../scanner-ai.js';
import { 
  getListaByIdLocal, saveListaItemLocal, saveListaLocal,
  registerItemCotacao, getMultiStoreQuotesForList, getFrequentStoresFromNotas, getCotacoesByEAN,
  cleanProductDescription, optimizeShoppingListBackend, saveCotacoesLocal, syncLocalCotacoesToBackend
} from '../db.js';
import { escapeHTML } from '../security.js';
import { authManager } from '../auth-manager.js';
import { getCategoryIcon, categorizeItem } from '../categorizer.js';

let activeListaCotacaoScanner = null;

export function stopActiveListaCotacaoScanner() {
  if (activeListaCotacaoScanner) {
    try {
      activeListaCotacaoScanner.stop();
    } catch (e) {
      console.warn('[ListaCotacao] Error stopping camera:', e);
    }
    activeListaCotacaoScanner = null;
  }
}

function formatBRL(val) {
  return 'R$ ' + (Number(val) || 0).toFixed(2).replace('.', ',');
}

function formatStoreShortName(name, maxWords = 2) {
  if (!name) return '';
  const trimmed = String(name).trim();
  const cleaned = trimmed.replace(/\b(ltda|s\/?a|s\.a|eireli|me|epp)\b\.?/gi, '').trim();
  const words = (cleaned || trimmed).split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return cleaned || trimmed;
  return words.slice(0, maxWords).join(' ') + '...';
}

function formatAIErrorMessage(err) {
  if (err?.name === 'AbortError' || err?.message?.includes('aborted') || err?.message?.includes('AbortError')) {
    return null;
  }
  const msg = (err && (err.message || String(err))) || '';
  if (msg.includes('context deadline exceeded') || msg.includes('timeout') || msg.includes('timed out')) {
    return 'Tempo de resposta excedido. Aproxime a câmera da etiqueta de preço e tente novamente.';
  }
  if (msg.includes('503') || msg.includes('high demand') || msg.includes('UNAVAILABLE') || msg.includes('temporariamente')) {
    return 'O serviço de IA está temporariamente ocupado. Tente novamente em alguns segundos.';
  }
  if (msg.includes('429') || msg.includes('quota') || msg.includes('exceeded your current quota') || msg.includes('RATE_LIMIT')) {
    return 'Limite de requisições atingido. Aguarde alguns instantes antes de escanear novamente.';
  }
  if (msg.includes('GEMINI_KEY_MISSING') || msg.includes('chave')) {
    return 'Chave de API do Gemini não configurada no servidor (.env).';
  }
  if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('conexão')) {
    return 'Falha de conexão com o servidor. Verifique sua rede e tente novamente.';
  }
  return 'Não foi possível reconhecer a etiqueta. Tente enquadrar melhor e tirar outra foto.';
}

export async function renderListaCotacaoView(container, id, syncManager) {
  if (!id) {
    window.location.hash = '#listas';
    return;
  }

  let lista = null;
  let knownStores = [];
  let activeQuoteStore = localStorage.getItem('last_active_quote_store') || '';
  let currentScanMode = 'ai-label'; // 'ai-label' | '1d-barcode'
  let currentAbortController = null;
  let isCameraOn = localStorage.getItem('lista_cotacao_camera_on') !== 'false';
  let itemsSearchFilter = '';

  // 1. Initial quick load from local cache
  const savedCustom = JSON.parse(localStorage.getItem('nfce_custom_stores') || '[]');
  knownStores = [...savedCustom];
  if (!activeQuoteStore && knownStores.length > 0) {
    activeQuoteStore = knownStores[0];
  }

  lista = await getListaByIdLocal(Number(id)).catch(() => null);

  // If we already have the list locally, render immediately (0 latency)
  if (lista) {
    render();
  } else {
    // Show clean loading state while fetching from cloud for the first time
    container.innerHTML = `
      <div class="chart-card text-center" style="padding: 3rem 1.5rem; margin-top: 1.5rem; border-radius: 1rem;">
        <div style="width: 42px; height: 42px; border: 3.5px solid var(--border-color); border-top-color: var(--color-primary, #2563EB); border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 1.25rem auto;"></div>
        <h3 style="font-size: 1.15rem; font-weight: 800; color: var(--text-main); margin-bottom: 0.35rem;">Carregando Cotação...</h3>
        <p class="text-muted" style="font-size: 0.85rem; margin: 0;">Buscando os produtos e cotações da lista...</p>
      </div>
    `;
  }

  // 2. Background cloud sync and store enrichment (non-blocking)
  async function syncBackgroundData() {
    // Sync store names from NFC-e notes
    getFrequentStoresFromNotas().then(nfStores => {
      const combined = Array.from(new Set([...(nfStores || []), ...savedCustom])).filter(Boolean);
      if (combined.length > 0) {
        knownStores = combined;
        if (!activeQuoteStore && knownStores.length > 0) {
          activeQuoteStore = knownStores[0];
        }
      }
    }).catch(() => {});

    // Sync cloud list and quotes
    if (authManager.isAuthenticated()) {
      try {
        const listRes = await authManager.fetchWithAuth(`/api/listas/${id}`);
        if (listRes.ok) {
          const cloudLista = await listRes.json();
          if (cloudLista && window.location.hash.startsWith(`#lista-cotacao/${id}`)) {
            const hasChanges = !lista || JSON.stringify(cloudLista.items?.map(i => i.id)) !== JSON.stringify(lista.items?.map(i => i.id));
            lista = cloudLista;
            if (hasChanges || !document.getElementById('lista-cotacao-wrapper')) {
              render();
            }
          }
        }
      } catch (e) {
        console.warn('[renderListaCotacaoView] Cloud list fetch error:', e);
      }

      // Background pull recent quotes & push unsynced quotes
      try {
        const cotRes = await authManager.fetchWithAuth('/api/cotacoes/recentes?limit=150');
        if (cotRes.ok) {
          const cotData = await cotRes.json();
          if (Array.isArray(cotData.cotacoes) && cotData.cotacoes.length > 0) {
            await saveCotacoesLocal(cotData.cotacoes);
            const quoteStores = cotData.cotacoes.map(c => c.mercado).filter(Boolean);
            knownStores = Array.from(new Set([...knownStores, ...quoteStores]));
            if (!activeQuoteStore && cotData.cotacoes[0]?.mercado) {
              activeQuoteStore = cotData.cotacoes[0].mercado;
            }
            if (window.location.hash.startsWith(`#lista-cotacao/${id}`)) {
              populateExistingQuotes().catch(() => {});
            }
          }
        }
        syncLocalCotacoesToBackend().catch(() => {});
      } catch (e) {
        console.warn('[renderListaCotacaoView] Sync cotacoes background error:', e);
      }
    }

    if (!lista && window.location.hash.startsWith(`#lista-cotacao/${id}`)) {
      container.innerHTML = `
        <div class="chart-card text-center" style="padding: 2.5rem 1.5rem; margin-top: 1.5rem;">
          <span class="material-symbols-outlined" style="font-size: 3.5rem; color: #94A3B8;">checklist</span>
          <h3 style="font-size: 1.2rem; font-weight: 700; color: #111827; margin: 0.5rem 0;">Lista não encontrada</h3>
          <p class="text-muted" style="font-size: 0.875rem; margin-bottom: 1.5rem;">A lista de compras solicitada não existe ou foi removida.</p>
          <a href="#listas" class="btn btn-primary" style="border-radius: var(--radius-pill); font-weight: 700; display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.6rem 1.2rem;">
            <span class="material-symbols-outlined">arrow_back</span> Voltar para Listas
          </a>
        </div>
      `;
    }
  }

  // Fire background sync asynchronously
  syncBackgroundData();

  function render() {
    const listTitle = lista.name || lista.nome || 'Lista de Compras';
    window.dispatchEvent(new CustomEvent('nfce-page-title-changed', { detail: { title: `Cotação: ${listTitle}` } }));

    const items = lista.items || [];

    container.innerHTML = `
      <div id="lista-cotacao-wrapper" style="padding-bottom: 95px; transition: padding-bottom 0.15s ease;">
        
        <!-- Header & Back Navigation -->
        <div class="detail-header" style="margin-bottom: 0.75rem;">
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; width: 100%;">
            <div style="display: flex; align-items: center; gap: 0.65rem; min-width: 0; flex: 1;">
              <button class="detail-back" id="btn-back-to-listas" style="background: var(--bg-surface-dim); border: none; color: var(--text-main); cursor: pointer; padding: 0.45rem; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                <span class="material-symbols-outlined" style="font-size: 20px;">arrow_back</span>
              </button>
              <div style="min-width: 0; flex: 1;">
                <h2 style="margin: 0; font-size: clamp(1.1rem, 4vw, 1.4rem); font-weight: 800; color: var(--text-main); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                  ${escapeHTML(listTitle)}
                </h2>
                <div style="font-size: 0.75rem; color: var(--text-muted);">
                  Cotação ao vivo na gôndola &bull; ${items.length} produtos
                </div>
              </div>
            </div>

            <a href="#lista-comparativo/${id}" id="btn-open-matrix-optimizer" class="btn" style="height: 36px; padding: 0 0.85rem; border-radius: var(--radius-pill); font-size: 0.78rem; font-weight: 700; border: 1px solid var(--border-color); background: var(--bg-surface-alt); color: var(--text-main); display: inline-flex; align-items: center; justify-content: center; gap: 0.3rem; white-space: nowrap; flex-shrink: 0; text-decoration: none;">
              <span class="material-symbols-outlined" style="font-size: 17px; color: #10B981;">analytics</span>
              <span>Comparativo</span>
            </a>
          </div>
        </div>

        <!-- 1. Integrated Camera Scanner Viewport -->
        <div class="scanner-viewport-wrapper product-viewport" style="position: relative; border-radius: 1rem; overflow: hidden; background: #000; border: 1px solid var(--border-color); box-shadow: var(--shadow-card); width: 100%; aspect-ratio: 16 / 10; max-height: 230px; margin: 0 auto 0.65rem auto;">
          <div id="cotacao-qr-reader" class="scanner-video-container" style="width: 100%; height: 100%;"></div>

          <!-- Live Mode Instructions Banner -->
          <div id="scanner-instruction-banner" style="position: absolute; top: 8px; left: 8px; right: 8px; background: rgba(15, 23, 42, 0.75); backdrop-filter: blur(8px); border: 1px solid rgba(255,255,255,0.15); border-radius: 0.5rem; padding: 0.35rem 0.6rem; display: flex; align-items: center; justify-content: space-between; z-index: 10; pointer-events: none;">
            <div style="display: flex; align-items: center; gap: 0.35rem; font-size: 0.74rem; font-weight: 600; color: #FFFFFF;">
              <span class="material-symbols-outlined" style="font-size: 15px; color: var(--color-primary-text, #60a5fa);">crop_free</span>
              <span id="scanner-instruction-text">Enquadre a etiqueta de preço e clique em Capturar</span>
            </div>
          </div>

          <!-- Quick Camera Controls (Zoom & Torch) -->
          <div class="camera-controls-toolbar" id="camera-controls" style="position: absolute; bottom: 8px; left: 0; right: 0; display: flex; justify-content: center; align-items: center; gap: 0.5rem; z-index: 10; pointer-events: auto;">
            <button class="zoom-btn" data-zoom="1" style="background: rgba(15,23,42,0.75); border: 1px solid rgba(255,255,255,0.25); color: white; border-radius: 9999px; width: 34px; height: 34px; font-size: 0.75rem; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(10px); transition: all 150ms ease;">1x</button>
            <button class="zoom-btn active" data-zoom="1.8" style="background: var(--color-primary, #2563EB); border: 1px solid var(--color-primary, #2563EB); color: #FFF; border-radius: 9999px; width: 34px; height: 34px; font-size: 0.75rem; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(10px); transition: all 150ms ease;">1.8x</button>
            <button class="zoom-btn" data-zoom="2.5" style="background: rgba(15,23,42,0.75); border: 1px solid rgba(255,255,255,0.25); color: white; border-radius: 9999px; width: 34px; height: 34px; font-size: 0.75rem; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(10px); transition: all 150ms ease;">2.5x</button>
            <button class="torch-btn" id="btn-torch-cotacao" style="background: rgba(15,23,42,0.75); border: 1px solid rgba(255,255,255,0.25); color: white; border-radius: 9999px; width: 34px; height: 34px; cursor: pointer; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(10px); transition: all 150ms ease;">
              <span class="material-symbols-outlined" style="font-size: 17px;">flashlight_on</span>
            </button>
          </div>
        </div>

        <!-- Camera Shutter Bar (Directly below camera) -->
        <div class="camera-shutter-bar" style="margin-bottom: 0.65rem;">
          <!-- Gallery Upload Button -->
          <button type="button" class="btn-camera-gallery" id="btn-upload-cotacao-file" title="Escolher Foto da Galeria">
            <span class="material-symbols-outlined" style="font-size: 22px;">photo_library</span>
          </button>

          <!-- Round Camera Shutter Trigger Button -->
          <button type="button" class="btn-camera-shutter" id="btn-capture-cotacao-frame" title="Tirar Foto da Etiqueta">
            <div class="shutter-inner-circle">
              <span class="material-symbols-outlined" style="font-size: 26px;">photo_camera</span>
            </div>
          </button>

          <!-- Toggle Camera On/Off Button (Manual Quote Mode) -->
          <button type="button" class="btn-camera-gallery" id="btn-toggle-camera-power" title="${isCameraOn ? 'Desligar Câmera (Modo Manual)' : 'Ligar Câmera'}" style="background: var(--bg-surface-alt); border: 1px solid var(--border-color); color: var(--text-main); width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.15s ease;">
            <span class="material-symbols-outlined" id="icon-camera-power" style="font-size: 22px; color: ${isCameraOn ? '#10B981' : '#EF4444'};">${isCameraOn ? 'videocam' : 'videocam_off'}</span>
          </button>
        </div>

        <!-- Mode Selector Tabs (Etiqueta | Cód. Barras) -->
        <div id="cotacao-mode-tabs" class="home-scope-switcher" style="margin-bottom: 0.75rem; width: 100%; box-sizing: border-box;">
          <button id="tab-mode-ai" class="home-scope-pill active" style="flex: 1; text-align: center; justify-content: center; font-size: 0.8rem; padding: 0.45rem 0.6rem; display: inline-flex; align-items: center; gap: 0.3rem;">
            <span class="material-symbols-outlined" style="font-size: 16px;">label</span>
            <span>Etiqueta com IA</span>
            <span class="badge-pro" style="margin-left: 0.2rem;"><span class="material-symbols-outlined">diamond</span>PRO</span>
          </button>
          <button id="tab-mode-1d" class="home-scope-pill" style="flex: 1; text-align: center; justify-content: center; font-size: 0.8rem; padding: 0.45rem 0.6rem; display: inline-flex; align-items: center; gap: 0.3rem;">
            <span class="material-symbols-outlined" style="font-size: 16px;">barcode_scanner</span>
            <span>Cód. Barras</span>
          </button>
        </div>

        <!-- 2. Modern Custom Store Select Below Camera & Controls (Sticky on scroll) -->
        <div class="sticky-store-selector-wrapper">
          <div id="scanner-store-bar" style="background: var(--bg-surface-alt); border: 1px solid var(--border-color); border-radius: 0.85rem; padding: 0.75rem 0.95rem; box-shadow: 0 4px 14px rgba(0,0,0,0.06);">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.45rem; gap: 0.5rem;">
              <label for="select-quote-store" style="font-size: 0.72rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.04em; display: flex; align-items: center; gap: 0.35rem; cursor: pointer; white-space: nowrap; flex-shrink: 0;">
                <span class="material-symbols-outlined" style="font-size: 17px; color: var(--color-primary-text, #2563EB);">storefront</span>
                <span>Local:</span>
              </label>
              <div style="display: flex; align-items: baseline; gap: 0.25rem; white-space: nowrap; flex-shrink: 0;" title="Total no local selecionado">
                <span id="active-store-running-total" class="font-mono font-bold" style="font-size: 0.92rem; color: #10B981;">
                  R$ 0,00
                </span>
              </div>
            </div>

            <div style="position: relative; width: 100%;">
              <select id="select-quote-store" class="custom-store-select" style="width: 100%; appearance: none; -webkit-appearance: none; background: var(--bg-surface); border: 1.5px solid var(--border-color); color: var(--text-main); font-size: 0.92rem; font-weight: 800; border-radius: 0.65rem; padding: 0.65rem 2.5rem 0.65rem 0.85rem; cursor: pointer; outline: none; transition: border-color 0.15s ease, box-shadow 0.15s ease;">
                <option value="" disabled ${!activeQuoteStore ? 'selected' : ''}>-- Selecione o Supermercado --</option>
                ${knownStores.map(st => `
                  <option value="${escapeHTML(st)}" ${st.toLowerCase() === (activeQuoteStore || '').toLowerCase() ? 'selected' : ''}>
                    🏪 ${escapeHTML(st)}
                  </option>
                `).join('')}
                <option value="__NEW_STORE__">➕ Digitar outro supermercado...</option>
              </select>
              <span class="material-symbols-outlined" style="position: absolute; right: 0.75rem; top: 50%; transform: translateY(-50%); pointer-events: none; color: var(--color-primary-text, #2563EB); font-size: 22px;">
                unfold_more
              </span>
            </div>

            <!-- Quick On-Screen Products Search Field -->
            <div style="position: relative; width: 100%; margin-top: 0.45rem;">
              <span class="material-symbols-outlined" style="position: absolute; left: 0.8rem; top: 50%; transform: translateY(-50%); pointer-events: none; color: var(--text-muted); font-size: 20px;">
                search
              </span>
              <input 
                type="text" 
                id="input-search-cotacao-items" 
                placeholder="Buscar produtos na tela..." 
                value="${escapeHTML(itemsSearchFilter)}"
                autocomplete="off" 
                style="width: 100%; box-sizing: border-box; background: var(--bg-surface); border: 1.5px solid var(--border-color); color: var(--text-main); font-size: 0.92rem; font-weight: 700; border-radius: 0.65rem; padding: 0.65rem 2.5rem 0.65rem 2.45rem; outline: none; transition: border-color 0.15s ease, box-shadow 0.15s ease; font-family: var(--font-body);" 
              />
              <button 
                type="button" 
                id="btn-clear-cotacao-search" 
                title="Limpar busca" 
                style="position: absolute; right: 0.65rem; top: 50%; transform: translateY(-50%); background: var(--bg-surface-dim); border: none; width: 24px; height: 24px; border-radius: 50%; color: var(--text-muted); cursor: pointer; display: ${itemsSearchFilter ? 'flex' : 'none'}; align-items: center; justify-content: center; transition: all 0.15s ease;"
              >
                <span class="material-symbols-outlined" style="font-size: 16px; font-weight: 700;">close</span>
              </button>
            </div>
          </div>
        </div>

        <input type="file" id="cotacao-camera-input" accept="image/*" capture="environment" style="display: none;" />
        <input type="file" id="cotacao-gallery-input" accept="image/*" style="display: none;" />

        <!-- 3. List of Products to be Quoted (Positioned below the camera like recent scanned items) -->
        <div class="chart-card" style="width: 100%; padding: 1.15rem 1.25rem; box-sizing: border-box; margin-bottom: 1.5rem; background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 1rem; box-shadow: var(--shadow-card);">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.85rem;">
            <div style="display: flex; align-items: center; gap: 0.45rem;">
              <span class="material-symbols-outlined" style="font-size: 22px; color: var(--color-primary-text, #2563EB);">checklist</span>
              <h3 style="margin: 0; font-size: 1.05rem; font-weight: 800; color: var(--text-main);">
                Produtos a Cotar
              </h3>
            </div>
            <span id="badge-cotacao-items-count" style="background: var(--color-primary-bg, #EFF6FF); border: 1px solid var(--color-primary-border, #BFDBFE); color: var(--color-primary-text, #2563EB); font-size: 0.75rem; font-weight: 700; padding: 0.2rem 0.6rem; border-radius: var(--radius-pill);">
              ${items.length} itens
            </span>
          </div>

          <div id="cotacao-products-list" style="display: flex; flex-direction: column; gap: 0.65rem;">
            ${items.length === 0 ? `
              <div style="text-align: center; padding: 2rem 1rem; color: var(--text-muted); font-size: 0.85rem;">
                <span class="material-symbols-outlined" style="font-size: 36px; opacity: 0.4; display: block; margin-bottom: 0.5rem;">playlist_add</span>
                Sua lista está vazia.<br/>
                Aponte a câmera para os produtos na gôndola para adicionar e cotar automaticamente!
              </div>
            ` : items.map(it => {
              const cleanEan = (it.barcode || it.ean || '').trim();
              const qty = it.quantity || it.quantidade || 1;
              const unit = it.unit || it.unidade || 'UN';
              const rawDesc = it.description || it.descricao || 'Item';
              const itemDesc = cleanProductDescription(rawDesc) || rawDesc;
              const catName = it.categoryName || it.categoriaNome || 'Outros';
              const icon = getCategoryIcon(catName);

              return `
                <div class="quote-product-row" id="quote-card-item-${it.id}" data-item-id="${it.id}" data-ean="${cleanEan}" data-search-text="${escapeHTML(`${itemDesc} ${cleanEan} ${catName}`)}" style="padding: 0.9rem; border-radius: 0.85rem; background: var(--bg-surface-alt); border: 1px solid var(--border-color); display: flex; flex-direction: column; gap: 0.55rem; transition: all 0.25s ease;">
                  <!-- Row 1: Full-width Product Info Header & Best Quote -->
                  <div style="display: flex; align-items: center; gap: 0.5rem; width: 100%;">
                    <span style="font-size: 1.35rem; line-height: 1;">${icon}</span>
                    <div style="min-width: 0; flex: 1;">
                      <div style="font-weight: 800; font-size: 0.92rem; color: var(--text-main); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                        ${escapeHTML(itemDesc)}
                      </div>
                      <div style="display: flex; align-items: center; gap: 0.4rem; font-size: 0.74rem; color: var(--text-muted); flex-wrap: wrap; margin-top: 0.15rem;">
                        ${cleanEan ? `<span class="font-mono">${cleanEan}</span>` : '<span style="color: #F59E0B; font-weight: 700;">(Manual)</span>'}
                        <span id="quote-item-best-badge-${it.id}" style="color: #059669; font-weight: 700;"></span>
                      </div>
                    </div>
                  </div>

                  <!-- Row 2: Full-width Store Label -->
                  <div style="font-size: 0.78rem; color: var(--text-muted); font-weight: 600; padding-top: 0.35rem; border-top: 1px dashed var(--border-color);" title="Preço no ${escapeHTML(activeQuoteStore || 'Local')}">
                    Preço no <strong style="color: var(--text-main); font-weight: 800;">${escapeHTML(formatStoreShortName(activeQuoteStore || 'Local', 2))}</strong>:
                  </div>

                  <!-- Row 3: Quantity Stepper + Price Input + Save Button (All on the same line) -->
                  <div style="display: flex; align-items: center; gap: 0.5rem; width: 100%;">
                    <!-- Quantity Stepper -->
                    <div style="display: flex; align-items: center; gap: 0.25rem; background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 0.5rem; padding: 0 0.35rem; height: 38px;">
                      <button type="button" class="btn-item-qty-minus" data-item-id="${it.id}" style="border: none; background: transparent; color: var(--text-main); font-weight: 800; font-size: 1.1rem; width: 26px; height: 100%; cursor: pointer; display: flex; align-items: center; justify-content: center;">−</button>
                      <span class="font-mono font-bold item-qty-display" data-item-id="${it.id}" style="font-size: 0.88rem; min-width: 22px; text-align: center; color: var(--text-main);">${qty}</span>
                      <button type="button" class="btn-item-qty-plus" data-item-id="${it.id}" style="border: none; background: transparent; color: var(--text-main); font-weight: 800; font-size: 1.1rem; width: 26px; height: 100%; cursor: pointer; display: flex; align-items: center; justify-content: center;">+</button>
                    </div>

                    <!-- Price Input -->
                    <div style="display: flex; align-items: center; gap: 0.25rem; background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 0.5rem; padding: 0 0.55rem; height: 38px; flex: 1; min-width: 90px;">
                      <span class="font-mono font-bold text-muted" style="font-size: 0.78rem;">R$</span>
                      <input type="number" step="0.01" min="0" inputmode="decimal" placeholder="0,00" class="input-item-quote-price font-mono font-bold" data-item-id="${it.id}" data-ean="${cleanEan}" data-desc="${escapeHTML(itemDesc)}" data-qty="${qty}" style="width: 100%; border: none; outline: none; background: transparent; font-size: 0.95rem; color: var(--text-main);" />
                    </div>

                    <!-- Save Button -->
                    <button type="button" class="btn-save-item-quote" data-item-id="${it.id}" data-ean="${cleanEan}" data-desc="${escapeHTML(itemDesc)}" title="Digite um preço para salvar" disabled style="background: #10B981; color: #FFF; border: none; border-radius: 0.5rem; height: 38px; padding: 0 0.9rem; font-weight: 700; font-size: 0.82rem; cursor: not-allowed; opacity: 0.45; display: inline-flex; align-items: center; justify-content: center; gap: 0.3rem; white-space: nowrap; transition: all 0.15s ease;">
                      <span class="material-symbols-outlined" style="font-size: 17px;">check</span>
                      <span>Salvar</span>
                    </button>
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </div>

        <!-- Sticky Floating Bottom Bar: Ver Comparativo & Ir às Compras -->
        <div style="position: fixed; bottom: 0; left: 0; right: 0; background: var(--bg-canvas); border-top: 1px solid var(--border-color); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); padding: 0.75rem 1rem; z-index: 50; display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; max-width: 768px; margin: 0 auto;">
          <a href="#lista-comparativo/${id}" class="btn" style="flex: 1; height: 42px; border-radius: var(--radius-pill); font-weight: 700; font-size: 0.85rem; border: 1px solid var(--border-color); background: var(--bg-surface-alt); color: var(--text-main); display: inline-flex; align-items: center; justify-content: center; gap: 0.35rem; text-decoration: none;">
            <span class="material-symbols-outlined" style="font-size: 18px; color: #10B981;">analytics</span>
            <span>Ver Comparativo</span>
          </a>
          
          <a href="#lista/${id}" class="btn btn-primary" style="flex: 1; height: 42px; border-radius: var(--radius-pill); font-weight: 700; font-size: 0.85rem; display: inline-flex; align-items: center; justify-content: center; gap: 0.35rem; text-decoration: none; cursor: pointer;">
            <span class="material-symbols-outlined" style="font-size: 18px;">shopping_cart</span>
            <span>Ir às Compras &rarr;</span>
          </a>
        </div>

        <!-- Progress / Loading Spinner Overlay for AI Capture -->
        <div id="ai-cotacao-progress-overlay" class="modal-overlay hidden" style="position: fixed; inset: 0; background: rgba(15, 23, 42, 0.78); backdrop-filter: blur(10px); display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 1000; padding: 1.5rem; text-align: center;">
          <div style="position: relative; width: 64px; height: 64px; margin-bottom: 1rem;">
            <div style="position: absolute; inset: 0; border: 4px solid rgba(255,255,255,0.2); border-top-color: #F59E0B; border-radius: 50%; animation: spin 1s linear infinite;"></div>
            <div style="position: absolute; inset: 8px; display: flex; align-items: center; justify-content: center; color: #F59E0B;">
              <span class="material-symbols-outlined" style="font-size: 26px;">photo_camera</span>
            </div>
          </div>
          <h3 id="ai-cotacao-progress-title" style="font-size: 1.15rem; font-weight: 800; color: #FFFFFF; margin: 0 0 0.25rem 0;">
            Identificando Produto...
          </h3>
          <p id="ai-cotacao-progress-desc" style="font-size: 0.84rem; color: #E2E8F0; margin: 0 0 1.25rem 0; max-width: 280px; line-height: 1.45;">
            Lendo informações da etiqueta...
          </p>
          <button type="button" id="btn-cancel-cotacao-progress" style="background: rgba(255, 255, 255, 0.15); border: 1px solid rgba(255, 255, 255, 0.3); color: #FFFFFF; padding: 0.5rem 1.25rem; border-radius: 9999px; font-size: 0.84rem; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 0.35rem;">
            <span class="material-symbols-outlined" style="font-size: 17px;">close</span>
            <span>Cancelar</span>
          </button>
        </div>

        <!-- Searchable Store Picker Modal -->
        <div id="modal-select-quote-store" class="modal-overlay hidden" style="position: fixed; inset: 0; background: rgba(0, 0, 0, 0.7); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 1rem;">
          <div class="chart-card" style="width: 100%; max-width: 440px; max-height: 85vh; display: flex; flex-direction: column; padding: 1.25rem; border-radius: 1rem; background: var(--bg-surface); box-shadow: var(--shadow-modal);">
            <!-- Modal Header -->
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.85rem;">
              <div style="display: flex; align-items: center; gap: 0.4rem;">
                <span class="material-symbols-outlined" style="font-size: 22px; color: var(--color-primary-text, #2563EB);">storefront</span>
                <h3 style="margin: 0; font-size: 1.05rem; font-weight: 800; color: var(--text-main);">
                  Selecionar Supermercado
                </h3>
              </div>
              <button type="button" id="btn-close-store-modal" style="background: none; border: none; color: var(--text-muted); cursor: pointer; padding: 0.2rem; display: flex; align-items: center; justify-content: center;">
                <span class="material-symbols-outlined" style="font-size: 22px;">close</span>
              </button>
            </div>

            <!-- Search Input -->
            <div style="margin-bottom: 0.85rem; position: relative;">
              <span class="material-symbols-outlined" style="position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); font-size: 18px; color: var(--text-muted);">search</span>
              <input type="text" id="input-search-quote-store" placeholder="Buscar supermercado..." style="width: 100%; box-sizing: border-box; padding: 0.6rem 0.8rem 0.6rem 2.25rem; border-radius: 0.65rem; border: 1px solid var(--border-color); background: var(--bg-surface-alt); color: var(--text-main); font-size: 0.88rem; outline: none;" />
            </div>

            <!-- Store List -->
            <div id="store-picker-list-container" style="flex: 1; overflow-y: auto; display: flex; flex-direction: column; gap: 0.45rem; max-height: 240px; margin-bottom: 1rem; padding-right: 0.2rem;">
              <!-- Populated dynamically via JS -->
            </div>

            <!-- Add Custom Store Section -->
            <div style="padding-top: 0.85rem; border-top: 1px solid var(--border-color);">
              <div style="font-size: 0.75rem; font-weight: 700; color: var(--text-muted); margin-bottom: 0.4rem;">Não encontrou na lista?</div>
              <div style="display: flex; gap: 0.4rem;">
                <input type="text" id="input-custom-quote-store" placeholder="Nome do novo estabelecimento..." style="flex: 1; box-sizing: border-box; padding: 0.55rem 0.75rem; border-radius: 0.5rem; border: 1px solid var(--border-color); background: var(--bg-surface-alt); color: var(--text-main); font-size: 0.84rem; outline: none;" />
                <button type="button" id="btn-confirm-custom-quote-store" class="btn btn-primary" style="padding: 0 0.85rem; border-radius: 0.5rem; font-size: 0.78rem; font-weight: 700; display: inline-flex; align-items: center; gap: 0.25rem; white-space: nowrap; cursor: pointer;">
                  <span class="material-symbols-outlined" style="font-size: 16px;">add</span>
                  <span>Usar</span>
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    `;

    // Event Handlers
    const btnBack = container.querySelector('#btn-back-to-listas');
    if (btnBack) {
      btnBack.addEventListener('click', () => {
        stopActiveListaCotacaoScanner();
        window.location.hash = '#listas';
      });
    }

    // Switch active store directly via shortcut pills
    container.querySelectorAll('.btn-select-quote-store').forEach(btn => {
      btn.addEventListener('click', () => {
        activeQuoteStore = btn.getAttribute('data-store');
        localStorage.setItem('last_active_quote_store', activeQuoteStore);
        render();
      });
    });

    // Modal Store Picker Controller
    const modalStorePicker = container.querySelector('#modal-select-quote-store');
    const storeListContainer = container.querySelector('#store-picker-list-container');
    const inputSearchStore = container.querySelector('#input-search-quote-store');
    const inputCustomStore = container.querySelector('#input-custom-quote-store');
    const btnConfirmCustom = container.querySelector('#btn-confirm-custom-quote-store');
    const btnCloseModal = container.querySelector('#btn-close-store-modal');

    function renderStoreListModal(filterText = '') {
      if (!storeListContainer) return;
      const cleanFilter = filterText.trim().toLowerCase();
      const filtered = knownStores.filter(st => !cleanFilter || st.toLowerCase().includes(cleanFilter));

      if (filtered.length === 0) {
        storeListContainer.innerHTML = `
          <div style="text-align: center; padding: 1.5rem 0.5rem; color: var(--text-muted); font-size: 0.84rem;">
            Nenhum estabelecimento encontrado com "${escapeHTML(filterText)}".<br/>
            Digite abaixo para cadastrar e usar agora.
          </div>
        `;
        return;
      }

      storeListContainer.innerHTML = filtered.map(st => {
        const isSelected = st.toLowerCase() === (activeQuoteStore || '').toLowerCase();
        return `
          <div class="store-modal-row" data-store="${escapeHTML(st)}" style="display: flex; align-items: center; justify-content: space-between; padding: 0.65rem 0.85rem; border-radius: 0.65rem; background: ${isSelected ? 'var(--color-primary-bg, #EFF6FF)' : 'var(--bg-surface-alt)'}; border: 1px solid ${isSelected ? 'var(--color-primary, #2563EB)' : 'var(--border-color)'}; cursor: pointer; transition: all 0.15s ease;">
            <div style="display: flex; align-items: center; gap: 0.5rem; min-width: 0; flex: 1;">
              <span class="material-symbols-outlined" style="font-size: 18px; color: ${isSelected ? 'var(--color-primary, #2563EB)' : 'var(--text-muted)'};">storefront</span>
              <span style="font-size: 0.88rem; font-weight: ${isSelected ? '800' : '600'}; color: ${isSelected ? 'var(--color-primary, #2563EB)' : 'var(--text-main)'}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                ${escapeHTML(st)}
              </span>
            </div>
            ${isSelected ? `
              <span class="material-symbols-outlined" style="font-size: 18px; color: var(--color-primary, #2563EB);">check_circle</span>
            ` : ''}
          </div>
        `;
      }).join('');

      storeListContainer.querySelectorAll('.store-modal-row').forEach(row => {
        row.addEventListener('click', () => {
          const storeName = row.getAttribute('data-store');
          if (storeName) {
            activeQuoteStore = storeName;
            localStorage.setItem('last_active_quote_store', activeQuoteStore);
            closeStorePickerModal();
            render();
          }
        });
      });
    }

    function openStorePickerModal() {
      if (modalStorePicker) {
        modalStorePicker.classList.remove('hidden');
        if (inputSearchStore) {
          inputSearchStore.value = '';
          setTimeout(() => inputSearchStore.focus(), 60);
        }
        renderStoreListModal('');
      }
    }

    function closeStorePickerModal() {
      if (modalStorePicker) {
        modalStorePicker.classList.add('hidden');
      }
    }

    if (inputSearchStore) {
      inputSearchStore.addEventListener('input', (e) => {
        renderStoreListModal(e.target.value);
      });
    }

    if (btnCloseModal) {
      btnCloseModal.addEventListener('click', closeStorePickerModal);
    }

    if (modalStorePicker) {
      modalStorePicker.addEventListener('click', (e) => {
        if (e.target === modalStorePicker) closeStorePickerModal();
      });
    }

    // Custom Store Select Controller
    const selectQuoteStore = container.querySelector('#select-quote-store');
    if (selectQuoteStore) {
      selectQuoteStore.addEventListener('change', (e) => {
        const selectedVal = e.target.value;
        if (selectedVal === '__NEW_STORE__') {
          openStorePickerModal();
          if (activeQuoteStore) {
            selectQuoteStore.value = activeQuoteStore;
          }
        } else if (selectedVal) {
          activeQuoteStore = selectedVal;
          localStorage.setItem('last_active_quote_store', activeQuoteStore);
          render();
        }
      });
    }

    // Fast on-screen products filter search controller
    const inputSearchItems = container.querySelector('#input-search-cotacao-items');
    const btnClearSearch = container.querySelector('#btn-clear-cotacao-search');
    const productRows = container.querySelectorAll('.quote-product-row');
    const badgeItemsCount = container.querySelector('#badge-cotacao-items-count');
    const productsListCont = container.querySelector('#cotacao-products-list');

    function filterProductsOnScreen(query = '') {
      itemsSearchFilter = query;
      const q = query.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      if (btnClearSearch) {
        btnClearSearch.style.display = query.trim() ? 'flex' : 'none';
      }

      let visibleCount = 0;
      productRows.forEach(row => {
        const searchText = (row.getAttribute('data-search-text') || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const match = !q || searchText.includes(q);
        row.style.display = match ? 'flex' : 'none';
        if (match) visibleCount++;
      });

      let emptyMsg = container.querySelector('#empty-search-cotacao-msg');
      if (visibleCount === 0 && q) {
        if (!emptyMsg && productsListCont) {
          emptyMsg = document.createElement('div');
          emptyMsg.id = 'empty-search-cotacao-msg';
          emptyMsg.style.cssText = 'text-align: center; padding: 2rem 1rem; color: var(--text-muted); font-size: 0.88rem;';
          emptyMsg.innerHTML = `
            <span class="material-symbols-outlined" style="font-size: 32px; opacity: 0.5; display: block; margin-bottom: 0.35rem;">search_off</span>
            <span>Nenhum produto encontrado para "<strong>${escapeHTML(query.trim())}</strong>"</span>
          `;
          productsListCont.appendChild(emptyMsg);
        } else if (emptyMsg) {
          emptyMsg.innerHTML = `
            <span class="material-symbols-outlined" style="font-size: 32px; opacity: 0.5; display: block; margin-bottom: 0.35rem;">search_off</span>
            <span>Nenhum produto encontrado para "<strong>${escapeHTML(query.trim())}</strong>"</span>
          `;
          emptyMsg.style.display = 'block';
        }
      } else if (emptyMsg) {
        emptyMsg.style.display = 'none';
      }

      if (badgeItemsCount) {
        if (q && visibleCount !== productRows.length) {
          badgeItemsCount.textContent = `${visibleCount} de ${productRows.length} itens`;
        } else {
          badgeItemsCount.textContent = `${productRows.length} itens`;
        }
      }
    }

    if (inputSearchItems) {
      inputSearchItems.addEventListener('input', (e) => {
        filterProductsOnScreen(e.target.value);
      });

      inputSearchItems.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          inputSearchItems.value = '';
          filterProductsOnScreen('');
        }
      });
    }

    if (btnClearSearch) {
      btnClearSearch.addEventListener('click', () => {
        if (inputSearchItems) {
          inputSearchItems.value = '';
          inputSearchItems.focus();
        }
        filterProductsOnScreen('');
      });
    }

    if (itemsSearchFilter) {
      filterProductsOnScreen(itemsSearchFilter);
    }

    // Trigger buttons to open store picker modal
    const btnTriggerPicker = container.querySelector('#btn-trigger-store-picker');
    const btnOpenPicker = container.querySelector('#btn-open-store-picker');
    const btnAddQuoteStore = container.querySelector('#btn-add-quote-store');

    if (btnTriggerPicker) btnTriggerPicker.addEventListener('click', openStorePickerModal);
    if (btnOpenPicker) btnOpenPicker.addEventListener('click', openStorePickerModal);
    if (btnAddQuoteStore) btnAddQuoteStore.addEventListener('click', openStorePickerModal);

    function handleAddCustomStore() {
      const name = inputCustomStore?.value?.trim();
      if (name) {
        const customStores = JSON.parse(localStorage.getItem('nfce_custom_stores') || '[]');
        if (!customStores.includes(name)) {
          customStores.push(name);
          localStorage.setItem('nfce_custom_stores', JSON.stringify(customStores));
        }
        if (!knownStores.includes(name)) {
          knownStores.push(name);
        }
        activeQuoteStore = name;
        localStorage.setItem('last_active_quote_store', activeQuoteStore);
        closeStorePickerModal();
        render();
      } else {
        inputCustomStore?.focus();
      }
    }

    if (btnConfirmCustom) {
      btnConfirmCustom.addEventListener('click', handleAddCustomStore);
    }
    if (inputCustomStore) {
      inputCustomStore.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleAddCustomStore();
        }
      });
    }

    // Quantity Stepper Handlers (+ / -)
    container.querySelectorAll('.btn-item-qty-plus').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const itemId = Number(btn.getAttribute('data-item-id'));
        const it = (lista.items || []).find(x => x.id === itemId);
        if (it) {
          const newQty = (it.quantity || it.quantidade || 1) + 1;
          it.quantity = newQty;
          it.quantidade = newQty;
          const disp = container.querySelector(`.item-qty-display[data-item-id="${itemId}"]`);
          if (disp) disp.textContent = newQty;
          await saveListaItemLocal(it);
          recalculateStoreTotal();
        }
      });
    });

    container.querySelectorAll('.btn-item-qty-minus').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const itemId = Number(btn.getAttribute('data-item-id'));
        const it = (lista.items || []).find(x => x.id === itemId);
        if (it) {
          const curr = it.quantity || it.quantidade || 1;
          if (curr > 1) {
            const newQty = curr - 1;
            it.quantity = newQty;
            it.quantidade = newQty;
            const disp = container.querySelector(`.item-qty-display[data-item-id="${itemId}"]`);
            if (disp) disp.textContent = newQty;
            await saveListaItemLocal(it);
            recalculateStoreTotal();
          }
        }
      });
    });

    // Populate existing quotes for items in the active store
    async function populateExistingQuotes() {
      if (!lista || !Array.isArray(lista.items)) return;
      let storeRunningTotal = 0;
      const totalElem = container.querySelector('#active-store-running-total');

      await Promise.all(lista.items.map(async (it) => {
        const cleanEan = (it.barcode || it.ean || '').trim();
        const qty = it.quantity || it.quantidade || 1;
        const input = container.querySelector(`.input-item-quote-price[data-item-id="${it.id}"]`);
        const badge = container.querySelector(`#quote-item-best-badge-${it.id}`);

        try {
          const eanQuotes = cleanEan ? await getCotacoesByEAN(cleanEan) : [];
          const itemQuotes = it.id ? await getCotacoesByEAN(`item_${it.id}`) : [];
          const quotes = [...eanQuotes, ...itemQuotes];

          if (quotes && quotes.length > 0) {
            if (activeQuoteStore) {
              const storeQuote = quotes.find(q => (q.mercado || '').trim().toLowerCase() === activeQuoteStore.toLowerCase());
              if (storeQuote && input && !input.value) {
                const p = typeof storeQuote.preco === 'number' ? storeQuote.preco : parseFloat(storeQuote.preco);
                if (p > 0) {
                  input.value = p.toFixed(2);
                  storeRunningTotal += (p * qty);
                  const saveBtn = container.querySelector(`.btn-save-item-quote[data-item-id="${it.id}"]`);
                  if (saveBtn) setButtonSavedState(saveBtn);
                }
              }
            }

            const bestQuote = quotes.reduce((min, q) => (q.preco < min.preco ? q : min), quotes[0]);
            if (bestQuote && badge) {
              badge.textContent = `🏆 Menor: R$ ${bestQuote.preco.toFixed(2).replace('.', ',')} no ${formatStoreShortName(bestQuote.mercado, 2)}`;
              badge.title = `Melhor preço: R$ ${bestQuote.preco.toFixed(2).replace('.', ',')} no ${bestQuote.mercado}`;
            }
          }
        } catch (e) {
          console.warn('[ListaCotacao] Quote load error for item', it.id, e);
        }
      }));

      if (totalElem) {
        totalElem.textContent = storeRunningTotal > 0 ? formatBRL(storeRunningTotal) : 'R$ 0,00';
      }
    }

    function setButtonSavedState(btn) {
      if (!btn) return;
      btn.setAttribute('data-saved', 'true');
      btn.disabled = false;
      btn.style.background = 'var(--bg-surface-dim)';
      btn.style.border = '1px solid var(--border-color)';
      btn.style.color = 'var(--text-main)';
      btn.style.opacity = '1';
      btn.style.cursor = 'pointer';
      btn.innerHTML = '<span class="material-symbols-outlined" style="font-size: 16px; color: #10B981;">check</span><span>Salvo</span>';
      btn.title = 'Preço salvo. Clique para salvar novamente.';
    }

    function setButtonUnsavedState(btn) {
      if (!btn) return;
      btn.removeAttribute('data-saved');
      btn.disabled = false;
      btn.style.background = '#10B981';
      btn.style.border = 'none';
      btn.style.color = '#FFF';
      btn.style.opacity = '1';
      btn.style.cursor = 'pointer';
      btn.innerHTML = '<span class="material-symbols-outlined" style="font-size: 17px;">check</span><span>Salvar</span>';
      btn.title = 'Salvar cotação';
    }

    function setButtonDisabledState(btn) {
      if (!btn) return;
      btn.removeAttribute('data-saved');
      btn.disabled = true;
      btn.style.background = '#10B981';
      btn.style.border = 'none';
      btn.style.color = '#FFF';
      btn.style.opacity = '0.45';
      btn.style.cursor = 'not-allowed';
      btn.innerHTML = '<span class="material-symbols-outlined" style="font-size: 17px;">check</span><span>Salvar</span>';
      btn.title = 'Digite um preço para salvar';
    }

    populateExistingQuotes();

    function recalculateStoreTotal() {
      let newTotal = 0;
      container.querySelectorAll('.input-item-quote-price').forEach(inp => {
        const p = parseFloat(inp.value.replace(',', '.') || '0');
        const itemId = Number(inp.getAttribute('data-item-id'));
        const it = (lista.items || []).find(x => x.id === itemId);
        const q = it ? (it.quantity || it.quantidade || 1) : 1;
        if (p > 0) newTotal += (p * q);
      });
      const totalElem = container.querySelector('#active-store-running-total');
      if (totalElem) totalElem.textContent = formatBRL(newTotal);
    }

    // Fast Save Quote per item (Manual Price Entry)
    container.querySelectorAll('.btn-save-item-quote').forEach(btn => {
      btn.addEventListener('click', async () => {
        if (btn.disabled) return;

        const itemId = Number(btn.getAttribute('data-item-id'));
        const ean = btn.getAttribute('data-ean') || '';
        const desc = btn.getAttribute('data-desc') || 'Produto';
        const input = container.querySelector(`.input-item-quote-price[data-item-id="${itemId}"]`);
        const priceVal = parseFloat(input?.value?.replace(',', '.') || '0');

        if (!activeQuoteStore) {
          syncManager.showToast('Selecione ou informe o supermercado no topo da tela.', 'warning');
          return;
        }

        if (isNaN(priceVal) || priceVal <= 0) {
          setButtonDisabledState(btn);
          return;
        }

        const originalHtml = btn.innerHTML;
        btn.innerHTML = '<span class="material-symbols-outlined" style="font-size: 15px;">sync</span>';
        btn.style.opacity = '0.7';

        try {
          await registerItemCotacao({
            listId: Number(id),
            itemId,
            ean,
            descricao: desc,
            mercado: activeQuoteStore,
            preco: priceVal
          });

          setButtonSavedState(btn);
          recalculateStoreTotal();
          // Sem mensagem de toast, mantendo fluxo silencioso e contínuo

        } catch (err) {
          console.error('Erro ao registrar cotação:', err);
          btn.innerHTML = originalHtml;
          btn.style.opacity = '1';
          syncManager.showToast('Erro ao salvar cotação. Tente novamente.', 'error');
        }
      });
    });

    // Enter key triggers save & input change handles button enabled/disabled state
    container.querySelectorAll('.input-item-quote-price').forEach(inp => {
      inp.addEventListener('input', () => {
        const itemId = inp.getAttribute('data-item-id');
        const saveBtn = container.querySelector(`.btn-save-item-quote[data-item-id="${itemId}"]`);
        const p = parseFloat(inp.value.replace(',', '.') || '0');
        if (!saveBtn) return;

        if (p > 0) {
          setButtonUnsavedState(saveBtn);
        } else {
          setButtonDisabledState(saveBtn);
        }
      });

      inp.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          const itemId = inp.getAttribute('data-item-id');
          const saveBtn = container.querySelector(`.btn-save-item-quote[data-item-id="${itemId}"]`);
          if (saveBtn && !saveBtn.disabled) saveBtn.click();
        }
      });
    });

    // --- 4. Camera & AI Scanner Setup ---
    const progressOverlay = container.querySelector('#ai-cotacao-progress-overlay');
    const progressTitle = container.querySelector('#ai-cotacao-progress-title');
    const progressDesc = container.querySelector('#ai-cotacao-progress-desc');
    const tabModeAI = container.querySelector('#tab-mode-ai');
    const tabMode1D = container.querySelector('#tab-mode-1d');
    const btnCaptureFrame = container.querySelector('#btn-capture-cotacao-frame');
    const btnUploadFile = container.querySelector('#btn-upload-cotacao-file');
    const cameraInput = container.querySelector('#cotacao-camera-input');
    const galleryInput = container.querySelector('#cotacao-gallery-input');

    function showProgress(title, desc) {
      if (progressOverlay) {
        if (progressTitle) progressTitle.textContent = title;
        if (progressDesc) progressDesc.textContent = desc;
        progressOverlay.classList.remove('hidden');
      }
    }

    function hideProgress() {
      if (progressOverlay) progressOverlay.classList.add('hidden');
    }

    // Handles scanned product payload (from 1D barcode or AI label recognition)
    async function handleScannedPayload(payload) {
      if (!payload) return;

      const rawEan = String(payload.ean || payload.gtin || payload.barcode || '').trim();
      const rawDesc = String(payload.descricao || payload.nome || payload.description || '').trim();
      const rawPreco = typeof payload.preco === 'number' && payload.preco > 0 
        ? payload.preco 
        : (typeof payload.rawPrecoGondola === 'number' ? payload.rawPrecoGondola : parseFloat(payload.preco) || 0);

      const targetStore = activeQuoteStore || 'Supermercado';

      // 1. Search if item already exists in the list (by EAN or fuzzy normalized description)
      const existingItems = lista.items || [];
      let matchedItem = null;

      if (rawEan) {
        matchedItem = existingItems.find(it => {
          const itEan = (it.barcode || it.ean || '').trim();
          return itEan && itEan === rawEan;
        });
      }

      if (!matchedItem && rawDesc) {
        const cleanDescScan = cleanProductDescription(rawDesc).toLowerCase();
        matchedItem = existingItems.find(it => {
          const cleanDescItem = cleanProductDescription(it.description || it.descricao || '').toLowerCase();
          return cleanDescItem && (cleanDescItem === cleanDescScan || cleanDescItem.includes(cleanDescScan) || cleanDescScan.includes(cleanDescItem));
        });
      }

      if (matchedItem) {
        // --- CASE 1: EXISTING ITEM (UPDATE) ---
        // 1. Update EAN and official description if missing
        let hasItemUpdates = false;
        if (rawEan && !(matchedItem.barcode || matchedItem.ean)) {
          matchedItem.barcode = rawEan;
          matchedItem.ean = rawEan;
          hasItemUpdates = true;
        }
        if (rawDesc && (!matchedItem.description || matchedItem.description.length < 4)) {
          matchedItem.description = rawDesc;
          hasItemUpdates = true;
        }
        if (hasItemUpdates) {
          await saveListaItemLocal(matchedItem);
        }

        // 2. Save quote in active store if price detected
        if (rawPreco > 0) {
          await registerItemCotacao({
            listId: Number(id),
            itemId: matchedItem.id,
            ean: rawEan || matchedItem.barcode || matchedItem.ean || '',
            descricao: matchedItem.description || rawDesc,
            mercado: targetStore,
            preco: rawPreco
          });

          // Update input in-place
          const input = container.querySelector(`.input-item-quote-price[data-item-id="${matchedItem.id}"]`);
          if (input) input.value = rawPreco.toFixed(2);
          recalculateStoreTotal();
        }

        // Highlight card with green pulse animation
        const cardElem = container.querySelector(`#quote-card-item-${matchedItem.id}`);
        if (cardElem) {
          cardElem.scrollIntoView({ behavior: 'smooth', block: 'center' });
          cardElem.style.borderColor = '#10B981';
          cardElem.style.boxShadow = '0 0 16px rgba(16, 185, 129, 0.4)';
          setTimeout(() => {
            cardElem.style.borderColor = 'var(--border-color)';
            cardElem.style.boxShadow = 'var(--shadow-card)';
          }, 2400);
        }

        syncManager.showToast(`✓ "${matchedItem.description}" atualizado: ${rawPreco > 0 ? formatBRL(rawPreco) : ''} no ${targetStore}!`, 'success');

      } else {
        // --- CASE 2: NEW ITEM (ADD TO LIST & QUOTE) ---
        const catInfo = categorizeItem(rawDesc || 'Outros');
        const newItemPayload = {
          listId: Number(id),
          description: rawDesc || (rawEan ? `Produto ${rawEan}` : 'Novo Produto'),
          barcode: rawEan,
          ean: rawEan,
          quantity: 1,
          unit: 'UN',
          estimatedPrice: rawPreco > 0 ? rawPreco : 0,
          actualPrice: 0,
          categoryName: catInfo?.nome || 'Outros',
          isChecked: false,
          actualQuantity: 0,
        };

        let createdItem = newItemPayload;
        if (authManager.isAuthenticated()) {
          try {
            const res = await authManager.fetchWithAuth(`/api/listas/${id}/itens`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(newItemPayload),
            });
            if (res.ok) {
              createdItem = await res.json();
            }
          } catch (e) {}
        }

        await saveListaItemLocal(createdItem);
        if (!lista.items) lista.items = [];
        lista.items.push(createdItem);

        if (rawPreco > 0) {
          await registerItemCotacao({
            listId: Number(id),
            itemId: createdItem.id,
            ean: rawEan,
            descricao: createdItem.description,
            mercado: targetStore,
            preco: rawPreco
          });
        }

        syncManager.showToast(`✓ Novo item "${createdItem.description}" adicionado à lista!`, 'success');
        render();

        // Highlight newly created card
        setTimeout(() => {
          const newCard = container.querySelector(`#quote-card-item-${createdItem.id}`);
          if (newCard) {
            newCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
            newCard.style.borderColor = '#10B981';
            newCard.style.boxShadow = '0 0 16px rgba(16, 185, 129, 0.4)';
            setTimeout(() => {
              newCard.style.borderColor = 'var(--border-color)';
              newCard.style.boxShadow = 'var(--shadow-card)';
            }, 2400);
          }
        }, 100);
      }
    }

    // Initialize Camera Instance
    activeListaCotacaoScanner = new AIScanner(
      'cotacao-qr-reader',
      async (barcodePayload) => {
        // 1D Barcode detected automatically by camera
        await handleScannedPayload(barcodePayload);
      },
      (err) => {
        console.warn('[ListaCotacao] Camera error:', err);
      }
    );

    function toggleCameraPower() {
      isCameraOn = !isCameraOn;
      localStorage.setItem('lista_cotacao_camera_on', String(isCameraOn));

      const icon = container.querySelector('#icon-camera-power');
      const btn = container.querySelector('#btn-toggle-camera-power');
      if (icon) {
        icon.textContent = isCameraOn ? 'videocam' : 'videocam_off';
        icon.style.color = isCameraOn ? '#10B981' : '#EF4444';
      }
      if (btn) {
        btn.title = isCameraOn ? 'Desligar Câmera (Modo Manual)' : 'Ligar Câmera';
      }

      setScanMode(currentScanMode);
      if (isCameraOn) {
        syncManager.showToast('Câmera ligada.', 'info');
      } else {
        syncManager.showToast('Câmera desligada. Modo manual ativo.', 'info');
      }
    }

    const btnToggleCamera = container.querySelector('#btn-toggle-camera-power');
    if (btnToggleCamera) {
      btnToggleCamera.addEventListener('click', toggleCameraPower);
    }

    function showCameraBlockedState(customMsg) {
      const videoCont = container.querySelector('#cotacao-qr-reader');
      if (videoCont) {
        videoCont.innerHTML = `
          <div style="width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #0F172A; color: #94A3B8; text-align: center; padding: 1.25rem;">
            <span class="material-symbols-outlined" style="font-size: 38px; margin-bottom: 0.35rem; color: #F59E0B;">no_photography</span>
            <span style="font-size: 0.9rem; font-weight: 800; color: #F8FAFC;">Câmera Indisponível</span>
            <span style="font-size: 0.75rem; margin-top: 0.2rem; color: #94A3B8; max-width: 250px; line-height: 1.35;">${customMsg || 'Não foi possível acessar a câmera do aparelho.'}</span>
            <div style="display: flex; gap: 0.5rem; margin-top: 0.75rem;">
              <button type="button" id="btn-retry-cotacao-camera" style="background: var(--color-primary, #2563EB); color: #FFF; border: none; border-radius: var(--radius-pill); font-size: 0.75rem; font-weight: 700; padding: 0.4rem 0.85rem; cursor: pointer; display: inline-flex; align-items: center; gap: 0.3rem;">
                <span class="material-symbols-outlined" style="font-size: 16px;">refresh</span>
                <span>Tentar Novamente</span>
              </button>
            </div>
          </div>
        `;
        const btnRetry = videoCont.querySelector('#btn-retry-cotacao-camera');
        if (btnRetry) {
          btnRetry.addEventListener('click', () => {
            isCameraOn = true;
            setScanMode(currentScanMode);
          });
        }
      }
    }

    function setScanMode(mode) {
      currentScanMode = mode;
      const banner = container.querySelector('#scanner-instruction-text');
      const shutterBar = container.querySelector('.camera-shutter-bar');
      const controls = container.querySelector('#camera-controls');
      const videoCont = container.querySelector('#cotacao-qr-reader');

      if (!isCameraOn) {
        if (activeListaCotacaoScanner) activeListaCotacaoScanner.stop();
        if (banner) banner.textContent = 'Câmera desligada • Modo de cotação manual';
        if (controls) controls.style.display = 'none';
        if (videoCont) {
          videoCont.innerHTML = `
            <div style="width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #0F172A; color: #94A3B8; text-align: center; padding: 1rem;">
              <span class="material-symbols-outlined" style="font-size: 38px; opacity: 0.5; margin-bottom: 0.35rem; color: #94A3B8;">videocam_off</span>
              <span style="font-size: 0.88rem; font-weight: 800; color: #E2E8F0;">Câmera Desligada</span>
              <span style="font-size: 0.75rem; margin-top: 0.15rem; color: #64748B;">Modo de cotação manual ativo</span>
              <button type="button" id="btn-turn-on-camera-viewport" style="margin-top: 0.65rem; background: var(--color-primary, #2563EB); color: #FFF; border: none; border-radius: var(--radius-pill); font-size: 0.75rem; font-weight: 700; padding: 0.4rem 0.95rem; cursor: pointer; display: inline-flex; align-items: center; gap: 0.3rem;">
                <span class="material-symbols-outlined" style="font-size: 16px;">videocam</span>
                <span>Ligar Câmera</span>
              </button>
            </div>
          `;
          const btnOn = videoCont.querySelector('#btn-turn-on-camera-viewport');
          if (btnOn) btnOn.addEventListener('click', toggleCameraPower);
        }
        return;
      }

      if (videoCont && (videoCont.querySelector('#btn-turn-on-camera-viewport') || videoCont.querySelector('#btn-retry-cotacao-camera'))) {
        videoCont.innerHTML = '';
      }

      if (mode === 'ai-label') {
        tabModeAI?.classList.add('active');
        tabMode1D?.classList.remove('active');
        if (banner) banner.textContent = 'Enquadre a etiqueta de preço e clique em Capturar';
        if (shutterBar) shutterBar.style.display = 'flex';
        if (controls) controls.style.display = 'flex';
        if (activeListaCotacaoScanner) {
          activeListaCotacaoScanner.start('ai-label').catch(err => {
            console.warn('[ListaCotacao] Camera start error:', err);
            showCameraBlockedState('Não foi possível iniciar a câmera para leitura de etiquetas.');
          });
        }
      } else {
        tabMode1D?.classList.add('active');
        tabModeAI?.classList.remove('active');
        if (banner) banner.textContent = 'Aponte a câmera para o código de barras (1D)';
        if (shutterBar) shutterBar.style.display = 'none';
        if (controls) controls.style.display = 'none';
        if (activeListaCotacaoScanner) {
          activeListaCotacaoScanner.start('1d-barcode').catch(err => {
            console.warn('[ListaCotacao] 1D Camera start error:', err);
            showCameraBlockedState('Não foi possível iniciar a câmera para código de barras.');
          });
        }
      }
    }

    if (tabModeAI) tabModeAI.addEventListener('click', () => setScanMode('ai-label'));
    if (tabMode1D) tabMode1D.addEventListener('click', () => setScanMode('1d-barcode'));

    // Start default camera mode
    setScanMode('ai-label');

    // Cancel Progress button handler
    const btnCancelProgress = container.querySelector('#btn-cancel-cotacao-progress');
    if (btnCancelProgress) {
      btnCancelProgress.addEventListener('click', () => {
        if (currentAbortController) {
          currentAbortController.abort();
          currentAbortController = null;
        }
        hideProgress();
        if (activeListaCotacaoScanner) {
          activeListaCotacaoScanner.unfreezePreview();
        }
        syncManager.showToast('Identificação cancelada.', 'info');
      });
    }

    // Capture Button Action (AI Label)
    if (btnCaptureFrame) {
      btnCaptureFrame.addEventListener('click', async () => {
        if (!activeListaCotacaoScanner) return;

        let frameBlob = null;
        try {
          frameBlob = await activeListaCotacaoScanner.captureFrame();
        } catch (captureErr) {
          console.warn('Direct frame capture failed:', captureErr);
          showCameraBlockedState('Não foi possível capturar o quadro da câmera.');
          syncManager.showToast('Câmera indisponível para captura direta.', 'error');
          return;
        }

        try {
          showProgress('Identificando produto...', 'Lendo informações da etiqueta');
          currentAbortController = new AbortController();
          const aiResult = await activeListaCotacaoScanner.analyzeWithGemini(frameBlob, (step) => {
            showProgress('Identificando produto...', typeof step === 'string' ? step : 'Processando imagem...');
          }, currentAbortController.signal);

          hideProgress();
          currentAbortController = null;
          if (activeListaCotacaoScanner) activeListaCotacaoScanner.unfreezePreview();
          await handleScannedPayload(aiResult);
        } catch (apiErr) {
          hideProgress();
          currentAbortController = null;
          if (activeListaCotacaoScanner) activeListaCotacaoScanner.unfreezePreview();
          const userMsg = formatAIErrorMessage(apiErr);
          if (userMsg) {
            syncManager.showToast(userMsg, 'error');
          }
        }
      });
    }

    const handleFileSelected = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
        showProgress('Carregando foto...', 'Lendo imagem da etiqueta');
        if (activeListaCotacaoScanner) {
          try {
            const url = URL.createObjectURL(file);
            activeListaCotacaoScanner.freezePreview(url);
          } catch (e) {}
        }
        currentAbortController = new AbortController();
        const aiResult = await activeListaCotacaoScanner.analyzeWithGemini(file, (step) => {
          showProgress('Identificando produto...', typeof step === 'string' ? step : 'Processando imagem...');
        }, currentAbortController.signal);
        hideProgress();
        currentAbortController = null;
        if (activeListaCotacaoScanner) activeListaCotacaoScanner.unfreezePreview();
        await handleScannedPayload(aiResult);
      } catch (err) {
        hideProgress();
        currentAbortController = null;
        if (activeListaCotacaoScanner) activeListaCotacaoScanner.unfreezePreview();
        const userMsg = formatAIErrorMessage(err);
        if (userMsg) {
          syncManager.showToast(userMsg, 'error');
        }
      } finally {
        if (cameraInput) cameraInput.value = '';
        if (galleryInput) galleryInput.value = '';
      }
    };

    if (cameraInput) cameraInput.addEventListener('change', handleFileSelected);
    if (galleryInput) galleryInput.addEventListener('change', handleFileSelected);
    if (btnUploadFile) btnUploadFile.addEventListener('click', () => galleryInput?.click());

    // Zoom Buttons
    container.querySelectorAll('.zoom-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        container.querySelectorAll('.zoom-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const zoom = parseFloat(btn.getAttribute('data-zoom') || '1.8');
        if (activeListaCotacaoScanner) activeListaCotacaoScanner.setZoom(zoom);
      });
    });

    // Torch Button
    const btnTorch = container.querySelector('#btn-torch-cotacao');
    if (btnTorch) {
      btnTorch.addEventListener('click', async () => {
        if (activeListaCotacaoScanner) {
          const state = await activeListaCotacaoScanner.toggleTorch();
          btnTorch.style.color = state ? '#60a5fa' : '#FFFFFF';
          btnTorch.style.borderColor = state ? '#60a5fa' : 'rgba(255,255,255,0.25)';
        }
      });
    }
  }

  render();
}
